import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface INocReimbursement {
  id: number;
  referenceNumber: string;
  referenceNumber1?: string;
  empId?: string;
  empID?: string;
  approverUserID?: string;
  approverName?: string;
  claimOrOnlyAdditi?: string;
  requestAmount?: string;
  approvedAmount?: string;
  dOJOnlineCourse?: string;
  courseCompletionDate?: string;
  durationOfOnlineCourse?: string;
  remarks?: string;
  contactNumber?: string;
  email?: string;
  requestStatus?: string | null;
  requestCreationDate?: string;
  created_at?: string;
  modified_at?: string;
  deleted_at?: string | null;
  is_draft?: boolean;
  endOfBlockDate?: string;
  eligibilityAmount?: string | number;
  reimbursement_type?: {
    type_id: string;
    type_title: string;
  };
  online_course?: any;
  course_category?: any;
  employee?: any;
  employeeName?: string;
  approver?: any;
  documents?: any[];
  remarks_data?: any[];
  work_history?: any[];
  status?: string;
}

export interface IReimbursementRequestType {
  id?: string | number;
  type_id?: string;
  type_title?: string;
  type_name?: string;
  description?: string;
}

export interface IReimbursementDocumentType {
  id?: string | number;
  type_id?: string;
  type_title?: string;
  type_name?: string;
  description?: string;
}

interface INocReimbursementState {
  data: INocReimbursement[];
  currentReimbursement: INocReimbursement | null;
  requestTypes: IReimbursementRequestType[];
  documentTypes: IReimbursementDocumentType[];
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  deleteLoading: boolean;
  requestTypesLoading: boolean;
  documentTypesLoading: boolean;
  total: number;
  page: number;
  limit: number;
}

const initialState: INocReimbursementState = {
  data: [],
  currentReimbursement: null,
  requestTypes: [],
  documentTypes: [],
  loading: false,
  error: null,
  createLoading: false,
  deleteLoading: false,
  requestTypesLoading: false,
  documentTypesLoading: false,
  total: 0,
  page: 1,
  limit: 10,
};

// Fetch all reimbursement requests
export const getAllNocReimbursements = createAsyncThunk(
  "nocReimbursements/getAll",
  async (
    payload: {
      query?: string;
      page?: number;
      limit?: number;
      orderColumn?: string;
      orderBy?: string;
      requestStatus?: string;
      sapStartDate?: string;
      sapEndDate?: string;
      startDate?: string;
      endDate?: string;
    } = {},
    thunkAPI
  ) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.FIND_ALL,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch single reimbursement request
export const getNocReimbursement = createAsyncThunk(
  "nocReimbursements/getOne",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.FIND_ONE,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch reimbursement request types
export const getReimbursementRequestTypes = createAsyncThunk(
  "nocReimbursements/getRequestTypes",
  async (_, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.REQUEST_TYPES,
        {}
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch reimbursement document types
export const getReimbursementDocumentTypes = createAsyncThunk(
  "nocReimbursements/getDocumentTypes",
  async (_, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.DOCUMENT_TYPES,
        {}
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create or update reimbursement request
export const createOrUpdateNocReimbursement = createAsyncThunk(
  "nocReimbursements/createOrUpdate",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.CREATE_OR_UPDATE,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Delete reimbursement request
export const deleteNocReimbursementRequest = createAsyncThunk(
  "nocReimbursements/delete",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.DELETE,
        payload
      );
      return { ...response.data, deletedId: payload.id };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Approve/Reject reimbursement request
export const approveRejectNocReimbursement = createAsyncThunk(
  "nocReimbursements/approveReject",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC_REIMBURSEMENT.APPROVE_REJECT,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const nocReimbursementSlice = createSlice({
  name: "nocReimbursements",
  initialState,
  reducers: {
    resetNocReimbursementState: (state) => {
      state.data = [];
      state.currentReimbursement = null;
      state.error = null;
      state.loading = false;
      state.createLoading = false;
      state.deleteLoading = false;
      state.total = 0;
      state.page = 1;
      state.limit = 10;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentReimbursement: (
      state,
      action: PayloadAction<number | INocReimbursement>
    ) => {
      if (typeof action.payload === "number") {
        const reimbursement = state.data.find((r) => r.id === action.payload);
        if (reimbursement) {
          state.currentReimbursement = reimbursement;
        }
      } else {
        state.currentReimbursement = action.payload;
      }
    },
    clearCurrentReimbursement: (state) => {
      state.currentReimbursement = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(getAllNocReimbursements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllNocReimbursements.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload?.data || action.payload || {};
        state.data = Array.isArray(responseData.data)
          ? responseData.data
          : Array.isArray(responseData)
          ? responseData
          : Array.isArray(action.payload)
          ? action.payload
          : [];
        state.total =
          responseData.total ??
          (Array.isArray(state.data) ? state.data.length : 0);
        state.page = responseData.page || 1;
        state.limit = responseData.limit || 10;
      })
      .addCase(getAllNocReimbursements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      // Fetch one
      .addCase(getNocReimbursement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNocReimbursement.fulfilled, (state, action) => {
        state.loading = false;
        const resData = action.payload?.data || action.payload;
        state.currentReimbursement = resData?.data || resData || null;
      })
      .addCase(getNocReimbursement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch request types
      .addCase(getReimbursementRequestTypes.pending, (state) => {
        state.requestTypesLoading = true;
        state.error = null;
      })
      .addCase(getReimbursementRequestTypes.fulfilled, (state, action) => {
        state.requestTypesLoading = false;
        const resData = action.payload?.data || action.payload;
        state.requestTypes = Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData)
          ? resData
          : [];
      })
      .addCase(getReimbursementRequestTypes.rejected, (state, action) => {
        state.requestTypesLoading = false;
        state.error = action.payload as string;
      })
      // Fetch document types
      .addCase(getReimbursementDocumentTypes.pending, (state) => {
        state.documentTypesLoading = true;
        state.error = null;
      })
      .addCase(getReimbursementDocumentTypes.fulfilled, (state, action) => {
        state.documentTypesLoading = false;
        const resData = action.payload?.data || action.payload;
        state.documentTypes = Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData)
          ? resData
          : [];
      })
      .addCase(getReimbursementDocumentTypes.rejected, (state, action) => {
        state.documentTypesLoading = false;
        state.error = action.payload as string;
      })
      // Create/Update
      .addCase(createOrUpdateNocReimbursement.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrUpdateNocReimbursement.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createOrUpdateNocReimbursement.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteNocReimbursementRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNocReimbursementRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.data = state.data.filter((r) => r.id !== action.payload.deletedId);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteNocReimbursementRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Approve/Reject
      .addCase(approveRejectNocReimbursement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRejectNocReimbursement.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveRejectNocReimbursement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetNocReimbursementState,
  clearError,
  setCurrentReimbursement,
  clearCurrentReimbursement,
} = nocReimbursementSlice.actions;
export default nocReimbursementSlice.reducer;
